import type { User } from '@supabase/supabase-js';
import { supabase } from '../config/supabase';
import type { UserRole } from '../types/user';
import { isSuperadmin, resolveUserRole } from './userRole';

export const LOGIN_MESSAGE_KEY = 'boris_login_message';

export type AccessBlockReason = 'user_inactive' | 'store_inactive' | 'no_store';

export interface UserAccess {
  role: UserRole | null;
  userActive: boolean;
  storeId: string | null;
  storeName: string | null;
  storeActive: boolean | null;
  canAccessDashboard: boolean;
  blockReason: AccessBlockReason | null;
}

export function accessBlockMessage(reason: AccessBlockReason | null): { title: string; message: string } {
  switch (reason) {
    case 'user_inactive':
      return {
        title: 'Cuenta deshabilitada',
        message:
          'Tu usuario fue deshabilitado por un administrador. Si creés que es un error, contactá al soporte de BorisMetrics.',
      };
    case 'store_inactive':
      return {
        title: 'Tienda suspendida',
        message:
          'El acceso al dashboard de esta tienda está suspendido. Contactá al administrador de la plataforma para reactivarla.',
      };
    case 'no_store':
      return {
        title: 'Sin tienda asignada',
        message: 'Tu cuenta no tiene una tienda vinculada. Pedile a un administrador que te asigne acceso.',
      };
    default:
      return {
        title: 'Acceso restringido',
        message: 'No podés acceder al dashboard en este momento.',
      };
  }
}

export function formatAuthError(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes('banned') || lower.includes('ban')) {
    return 'Tu cuenta está deshabilitada. Contactá al administrador.';
  }
  if (lower.includes('invalid login') || lower.includes('invalid credentials')) {
    return 'Email o contraseña incorrectos.';
  }
  if (lower.includes('email not confirmed')) {
    return 'Confirmá tu email antes de iniciar sesión.';
  }
  if (lower.includes('email rate') || lower.includes('rate limit')) {
    return 'Enviaste demasiados emails de recuperación. Esperá 1 hora o contactá al administrador.';
  }
  if (lower.includes('30 seconds')) {
    return 'Por seguridad, esperá 30 segundos antes de volver a intentar.';
  }
  return message;
}

export async function resolveUserAccess(user: User): Promise<UserAccess> {
  const role = await resolveUserRole(user);

  if (isSuperadmin(role)) {
    return {
      role,
      userActive: true,
      storeId: null,
      storeName: null,
      storeActive: null,
      canAccessDashboard: false,
      blockReason: 'no_store',
    };
  }

  const { data, error } = await supabase
    .from('users')
    .select('active, store_id, role, stores(store_name, active)')
    .eq('id', user.id)
    .maybeSingle();

  if (error || !data) {
    return {
      role,
      userActive: false,
      storeId: null,
      storeName: null,
      storeActive: null,
      canAccessDashboard: false,
      blockReason: 'no_store',
    };
  }

  const storeJoin = data.stores as { store_name?: string; active?: boolean } | { store_name?: string; active?: boolean }[] | null;
  const store = Array.isArray(storeJoin) ? storeJoin[0] : storeJoin;
  const userActive = data.active ?? true;
  const storeId = data.store_id ?? null;
  const storeActive = storeId ? (store?.active ?? true) : null;
  const storeName = store?.store_name ?? null;

  let blockReason: AccessBlockReason | null = null;
  if (!userActive) {
    blockReason = 'user_inactive';
  } else if (!storeId) {
    blockReason = 'no_store';
  } else if (storeActive === false) {
    blockReason = 'store_inactive';
  }

  return {
    role: (data.role as UserRole) ?? role,
    userActive,
    storeId,
    storeName,
    storeActive,
    canAccessDashboard: userActive && !!storeId && storeActive !== false,
    blockReason,
  };
}
